import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  InputField,
  MenuInput,
  MenuInputOption,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useCorrelationFields } from "./CorrelationFieldsContext";
import { ModalOverlay } from "./ModalOverlay";
import type {
  CorrelationRule,
  CorrelationRuleUserField,
} from "./correlationRuleCodec";

const messages = defineMessages({
  editTitle: {
    defaultMessage: "Edit Rule",
    id: "G/ym7R",
    description: "Edit rule modal title",
  },
  addTitle: {
    defaultMessage: "Add Rule",
    id: "FpOkOf",
    description: "Add rule modal title",
  },
  saveAction: {
    defaultMessage: "Save",
    id: "wJAtiD",
    description: "Primary action label for edit rule modal",
  },
  addAction: {
    defaultMessage: "Add Rule",
    id: "C1+kWj",
    description: "Primary action label for add rule modal",
  },
});

interface CorrelationRuleModalProps {
  isEditMode: boolean;
  isOpen: boolean;
  onClose: (open: boolean) => void;
  /** Save a single rule to the API. Modal stays open on failure. */
  onSave: (data: CorrelationRule) => Promise<void>;
  ruleData?: CorrelationRule | null;
}

let localId = 0;
function newId(): string {
  localId += 1;
  return `cr-modal-${Date.now()}-${localId}`;
}

function withSelected(
  options: MenuInputOption[],
  value: string,
): MenuInputOption[] {
  return options.map((opt) => ({ ...opt, isChecked: opt.value === value }));
}

function ModalRuleContent({
  rule,
  setRule,
}: {
  rule: CorrelationRule;
  setRule: (r: CorrelationRule) => void;
}) {
  const intl = useIntl();
  const { userFieldOptions, accountFieldOptions } = useCorrelationFields();

  const updateUserField = (
    userFieldId: string,
    patch: Partial<CorrelationRuleUserField>,
  ) => {
    setRule({
      ...rule,
      userFields: rule.userFields.map((uf) =>
        uf.id === userFieldId ? { ...uf, ...patch } : uf,
      ),
    });
  };

  const addUserField = () => {
    setRule({
      ...rule,
      userFields: [
        ...rule.userFields,
        { id: newId(), field: "", initialChar: "" },
      ],
    });
  };

  const removeUserField = (userFieldId: string) => {
    if (rule.userFields.length <= 1) return;
    setRule({
      ...rule,
      userFields: rule.userFields.filter((uf) => uf.id !== userFieldId),
    });
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border px-4 py-3"
      style={{
        borderColor: "var(--color-border-secondary-subtlest, #293950)",
      }}
    >
      {rule.userFields.map((uf, index) => {
        const isFirst = index === 0;
        const isLastUserRow = index === rule.userFields.length - 1;
        const canRemove = rule.userFields.length > 1;

        return (
          <div key={uf.id} className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 shrink-0">
              <Typography
                kind="body4"
                color="--color-foreground-neutral-subtle"
              >
                <FormattedMessage
                  id="k4dTkL"
                  defaultMessage="USERS"
                  description="Users column label"
                />
              </Typography>
              <div className="w-[200px]">
                <MenuInput
                  placeholder={intl.formatMessage({
                    id: "2pFGmp",
                    defaultMessage: "Select",
                    description: "Select user field placeholder",
                  })}
                  options={withSelected(userFieldOptions, uf.field)}
                  onChange={(opt) =>
                    opt && updateUserField(uf.id, { field: String(opt.value) })
                  }
                  isPortalEnabled
                  classNames={{ dropdownClassName: "!z-[10001]" }}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Typography
                kind="body4"
                color="--color-foreground-neutral-subtle"
              >
                <FormattedMessage
                  id="RVzYc3"
                  defaultMessage="INITIALCHAR"
                  description="Initial char operator label"
                />
              </Typography>
              <div className="w-[120px]">
                <InputField
                  inputmode="numeric"
                  name={`modal-initialChar-${uf.id}`}
                  placeholder=""
                  size="medium"
                  value={uf.initialChar}
                  setValue={(val: string) =>
                    updateUserField(uf.id, { initialChar: val })
                  }
                  regexPattern="[0-9]*"
                />
              </div>
            </div>

            {isFirst && (
              <div className="flex items-center gap-2 shrink-0">
                <Typography
                  kind="body4"
                  color="--color-foreground-neutral-subtle"
                >
                  <FormattedMessage
                    id="qwVkxz"
                    defaultMessage="ACCOUNT"
                    description="Account column label"
                  />
                </Typography>
                <div className="w-[200px]">
                  <MenuInput
                    placeholder={intl.formatMessage({
                      id: "Zc9+eX",
                      defaultMessage: "Select",
                      description: "Select account field placeholder",
                    })}
                    options={withSelected(
                      accountFieldOptions,
                      rule.accountField,
                    )}
                    onChange={(opt) =>
                      opt &&
                      setRule({ ...rule, accountField: String(opt.value) })
                    }
                    isPortalEnabled
                    classNames={{ dropdownClassName: "!z-[10001]" }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-1 ml-auto">
              {isLastUserRow && (
                <ButtonIcon
                  kind="outlinedNeutral"
                  icon={<Icon kind="Add" size="small" />}
                  ariaLabel={intl.formatMessage({
                    id: "w3jmgG",
                    defaultMessage: "Add user field",
                    description: "Add user field to rule aria label",
                  })}
                  onClick={addUserField}
                />
              )}
              {canRemove && (
                <ButtonIcon
                  kind="outlinedNeutral"
                  icon={<Icon kind="Subtract" size="small" />}
                  ariaLabel={intl.formatMessage({
                    id: "sXI+gC",
                    defaultMessage: "Remove user field",
                    description: "Remove user field from rule aria label",
                  })}
                  onClick={() => removeUserField(uf.id)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CorrelationRuleModal({
  isOpen,
  onClose,
  onSave,
  isEditMode = false,
  ruleData,
}: CorrelationRuleModalProps) {
  const intl = useIntl();
  const [rule, setRule] = useState<CorrelationRule>(
    () =>
      ruleData ?? {
        id: newId(),
        accountField: "",
        userFields: [{ id: newId(), field: "", initialChar: "" }],
      },
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRuleValid =
    rule.userFields.every((uf) => uf.field.trim() !== "") &&
    rule.accountField.trim() !== "";

  const title = intl.formatMessage(
    isEditMode ? messages.editTitle : messages.addTitle,
  );
  const primaryActionLabel = intl.formatMessage(
    isEditMode ? messages.saveAction : messages.addAction,
  );

  const handlePrimary = async () => {
    setError(null);
    setIsSaving(true);
    try {
      await onSave(rule);
      setIsSaving(false);
      onClose(false);
    } catch (e) {
      setIsSaving(false);
      setError(e instanceof Error ? e.message : "Failed to save rule");
    }
  };

  return (
    <>
      {isOpen && <ModalOverlay onClick={() => onClose(false)} />}
      <ModalIsland
        onClose={() => onClose(false)}
        isOpen={isOpen}
        title={title}
        className="!z-[10000] !max-w-[60rem]"
        contentBody={
          <div className="flex flex-col gap-3">
            {!!error && (
              <AlertBanner
                colorTheme="Critical"
                description={error}
                kind="low"
                title={intl.formatMessage({
                  id: "zMWw5h",
                  defaultMessage: "Error",
                  description: "Error alert title",
                })}
                onCancel={() => setError(null)}
                shouldAutoDismiss={false}
              />
            )}
            <ModalRuleContent rule={rule} setRule={setRule} />
          </div>
        }
        FooterActionBarComp={
          <FooterActionBar>
            <Box className="flex ml-auto gap-2">
              <Button
                kind="outlined"
                size="medium"
                onClick={() => onClose(false)}
              >
                <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
              </Button>
              <Button
                kind="filled"
                size="medium"
                isLoading={isSaving}
                isDisabled={!isRuleValid}
                onClick={() => {
                  void handlePrimary();
                }}
              >
                {primaryActionLabel}
              </Button>
            </Box>
          </FooterActionBar>
        }
      />
    </>
  );
}
